import React, { useState, useEffect } from 'react';
import Slider from '@mui/material/Slider';
import styles from './RankedModule.module.css';
import { calculateScoreMultiplier } from '../../utils/scoreMultiplier';
import { useGameSettings } from '../../contexts/GameSettingsContext';
import { getRankFromScore } from '../../utils/rankSystem';

interface RankedModuleProps {
  isRanked: boolean;
  onIsRankedChange: (value: boolean) => void;
}

const RankedModule: React.FC<RankedModuleProps> = ({
  isRanked,
  onIsRankedChange,
}) => {
  // Get difficulty settings directly from context to ensure fresh values
  const {
    isBlurry,
    blurAmount,
    hasTimeLimit,
    timeLimitMs,
    isCropped,
    cropPercentage,
    isFlipped,
    isInverted,
    isMosaic,
    isNoColor,
    artistRangeStart,
    setArtistRangeStart,
    artistRangeEnd,
    setArtistRangeEnd,
    artists,
  } = useGameSettings();

  const totalArtists = artists.length;
  
  // Calculate difficulty multiplier
  const difficultyMultiplier = calculateScoreMultiplier({
    isBlurry,
    blurAmount,
    hasTimeLimit,
    timeLimitMs,
    isCropped,
    cropPercentage,
    isFlipped,
    isInverted,
    isMosaic,
    isNoColor,
  });

  // Calculate artist count multiplier (only in ranked mode)
  const artistCountInRange = artistRangeEnd - artistRangeStart + 1;
  const artistCountMultiplier = isRanked ? artistCountInRange / 100 : 1.0;
  
  // Total multiplier includes both difficulty and artist count
  const totalScoreMultiplier = difficultyMultiplier * artistCountMultiplier;

  const [highScore, setHighScore] = useState<number | null>(null);
  const [highScoreDate, setHighScoreDate] = useState<string | null>(null);

  useEffect(() => {
    const storedHighScore = localStorage.getItem('rankedHighScore');
    const storedDate = localStorage.getItem('rankedHighScoreDate');
    
    if (storedHighScore) {
      setHighScore(parseFloat(storedHighScore));
    }
    if (storedDate) {
      setHighScoreDate(storedDate);
    }
  }, []);
  
  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className={styles.container}>
      <h2>Ranked Mode</h2>
      <div className={styles.toggle}>
        <label>
          <input
            type="checkbox"
            checked={isRanked}
            onChange={(e) => onIsRankedChange(e.target.checked)}
          />
          Enable Ranked
        </label>
      </div>
      {highScore !== null && (
        <div 
          className={styles.highScore}
          style={{
            background: getRankFromScore(highScore).background,
            borderColor: getRankFromScore(highScore).borderColor,
            boxShadow: `0 4px 12px ${getRankFromScore(highScore).shadowColor}`,
          }}
        >
          <div 
            className={styles.highScoreTitle}
            style={{ color: getRankFromScore(highScore).textColor }}
          >
            {getRankFromScore(highScore).name}
          </div>
          <div 
            className={styles.highScoreValue}
            style={{ 
              color: getRankFromScore(highScore).textColor,
              textShadow: getRankFromScore(highScore).textColor === '#fff' 
                ? '2px 2px 4px rgba(0, 0, 0, 0.3)' 
                : '1px 1px 2px rgba(255, 255, 255, 0.5)'
            }}
          >
            {highScore.toFixed(2)}
          </div>
          {highScoreDate && (
            <div 
              className={styles.highScoreDate}
              style={{ 
                color: getRankFromScore(highScore).textColor,
                opacity: 0.8 
              }}
            >
              {formatDate(highScoreDate)}
            </div>
          )}
        </div>
      )}
      <div className={styles.multiplier}>
        <span className={styles.multiplierLabel}>Difficulty Multiplier:</span>
        <span className={styles.multiplierValue}>{difficultyMultiplier.toFixed(2)}x</span>
      </div>
      {isRanked && (
        <div className={styles.multiplier}>
          <span className={styles.multiplierLabel}>Artist Count Multiplier:</span>
          <span className={styles.multiplierValue}>{artistCountMultiplier.toFixed(2)}x</span>
        </div>
      )}
      {isRanked && (
        <div className={`${styles.multiplier} ${styles.totalMultiplier}`}>
          <span className={styles.multiplierLabel}>Total Score Multiplier:</span>
          <span className={styles.multiplierValue}>{totalScoreMultiplier.toFixed(2)}x</span>
        </div>
      )}
      {isRanked && (
        <div className={styles.artistRange}>
          <label>Artist Range: {artistRangeStart} - {artistRangeEnd} ({artistRangeEnd - artistRangeStart + 1} artists)</label>
          <div className={styles.sliderWrapper}>
            <Slider
              value={[artistRangeStart, artistRangeEnd]}
              onChange={(_event, newValue) => {
                if (Array.isArray(newValue)) {
                  setArtistRangeStart(newValue[0]);
                  setArtistRangeEnd(newValue[1]);
                }
              }}
              valueLabelDisplay="auto"
              min={1}
              max={totalArtists}
              disableSwap
              sx={{
                color: '#2563eb',
                height: 6,
                '& .MuiSlider-thumb': {
                  height: 20,
                  width: 20,
                  backgroundColor: '#2563eb',
                  border: '2px solid #fff',
                  '&:hover': {
                    boxShadow: '0 0 0 8px rgba(37, 99, 235, 0.16)',
                  },
                },
                '& .MuiSlider-track': {
                  height: 6,
                  backgroundColor: '#2563eb',
                },
                '& .MuiSlider-rail': {
                  height: 6,
                  backgroundColor: '#d0dff9',
                },
                '& .MuiSlider-valueLabel': {
                  backgroundColor: '#2563eb',
                },
              }}
            />
          </div>
          <div className={styles.rangeValues}>
            <span>Start: {artistRangeStart}</span>
            <span>End: {artistRangeEnd}</span>
          </div>
        </div>
      )}
      <div className={styles.info}>
        <p>When ranked mode is active:</p>
        <ul>
          <li>10 rounds (locked)</li>
          <li>All artists included</li>
          <li>No favorites filter</li>
          <li>No difficulty filter</li>
          <li>Artist list cannot be edited</li>
        </ul>
      </div>
    </div>
  );
};

export default RankedModule;

